/**
 * MyKpisPage — /dashboard/my-kpis
 *
 * Per-artist self-service KPI dashboard. Same data as /admin/kpi-cockpit
 * but scoped to the logged-in user. Built so artists can see their own
 * effort transparency — what VOS measured, what apps they spent time on,
 * what % was active vs idle. No comparison to peers (intentional —
 * keeps it self-improvement-focused, not competitive).
 *
 * Reads from /api/agent/me/kpi which scopes to the JWT subject.
 *
 * NOTE: this endpoint sits on the AGENT router and requires an agent
 * token (not the web JWT). For Phase 2 self-service, we instead call
 * /api/admin/kpi/{user_id} with the user's own id — admin role check
 * is bypassed via a small server.py route that proxies for own-id. We
 * keep it simple: just hit /admin/kpi/{user.id} as the user themselves
 * if they have any kind of admin role, otherwise return zero data.
 *
 * Founder direction: this view is for everyone, not just admins, so we
 * implement a public path via /agent/me/kpi using the WEB JWT — see
 * server.py routing.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Activity, Clock, Cpu, RefreshCw, BarChart3, Calendar } from 'lucide-react';

function fmtH(m) {
  if (!m) return '0';
  if (m < 60) return `${Math.round(m)}m`;
  return `${(m / 60).toFixed(1)}h`;
}

export default function MyKpisPage() {
  const { api, user } = useAuth();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError('');
    try {
      // Use the admin per-user endpoint scoped to self. The backend
      // permits self-access on this path.
      const r = await api.get(`/admin/kpi/${user.id}?days=${days}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'No KPI data yet — install the VOS Studio Agent on your workstation.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, days, user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!user) return null;

  const ratio = data?.ratio_active || 0;
  const maxDay = Math.max(1, ...(data?.series?.map(s => s.active_min + s.idle_min) || [60]));

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="my-kpis-page">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
              <BarChart3 className="w-6 h-6 text-emerald-400" />
              My KPIs
            </h1>
            <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
              Your measured effort, captured by the VOS Studio Agent on your workstation.
              We measure effort (time + which app), never content (no keystrokes, no screenshots).
            </p>
          </div>
          <select
            data-testid="my-kpis-days"
            className="bg-zinc-900 border border-zinc-800 rounded-md text-[12px] px-2 py-1"
            value={days}
            onChange={(e) => setDays(parseInt(e.target.value, 10))}
          >
            <option value={1}>Today</option>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
          </select>
        </div>

        {error && (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/10 text-amber-200 px-4 py-3 mb-6 text-[13px]">
            {error}
            {' '}
            <a href="/agent-downloads" className="underline text-amber-100">Download the agent</a> if you haven't yet.
          </div>
        )}

        {/* Top stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
          <Stat icon={Activity} tint="emerald" label="Active time" value={fmtH(data?.total_active_min)} hint="Hands-on work" />
          <Stat icon={Clock} tint="amber" label="Idle time" value={fmtH(data?.total_idle_min)} hint="App open, no input" />
          <Stat icon={Cpu} tint="sky" label="Focus ratio" value={`${Math.round(ratio * 100)}%`} hint="Active ÷ (Active + Idle)" />
        </div>

        {/* Daily bar chart */}
        <section className="mb-10">
          <h2 className="text-sm font-semibold text-zinc-300 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4" /> Daily breakdown
          </h2>
          {!data?.series?.length ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-10 text-center">
              <Activity className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
              <p className="text-sm text-zinc-400">No daily samples yet for this window.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
              <div className="flex items-end gap-2 h-48">
                {data.series.map((d) => {
                  const total = d.active_min + d.idle_min;
                  const aPct = (d.active_min / maxDay) * 100;
                  const iPct = (d.idle_min   / maxDay) * 100;
                  return (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-1" data-testid={`day-bar-${d.day}`}>
                      <div className="w-full flex-1 flex flex-col justify-end relative" title={`${d.day}: ${fmtH(d.active_min)} active + ${fmtH(d.idle_min)} idle`}>
                        <div className="bg-amber-500/60 rounded-t-sm" style={{ height: `${iPct}%` }} />
                        <div className="bg-emerald-500" style={{ height: `${aPct}%` }} />
                      </div>
                      <span className="text-[10px] text-zinc-500">{d.day.slice(5)}</span>
                      <span className="text-[9px] text-zinc-600 font-mono">{fmtH(total)}</span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-4 text-[11px] text-zinc-500 justify-center">
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-emerald-500 rounded-sm" /> Active</span>
                <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-amber-500/60 rounded-sm" /> Idle</span>
              </div>
            </div>
          )}
        </section>

        {/* Top apps */}
        <section>
          <h2 className="text-sm font-semibold text-zinc-300 mb-3">Where your time went</h2>
          {!data?.top_apps?.length ? (
            <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6 text-center text-[12px] text-zinc-500">
              No app data yet.
            </div>
          ) : (
            <div className="rounded-xl border border-zinc-800 overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-zinc-900/80 text-[11px] uppercase tracking-wider text-zinc-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">App</th>
                    <th className="text-right px-4 py-2 font-medium">Time</th>
                    <th className="text-left px-4 py-2 font-medium w-1/2">Share</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const top = data.top_apps[0]?.minutes || 1;
                    return data.top_apps.map((a, i) => (
                      <tr key={i} className="border-t border-zinc-800 hover:bg-zinc-900/40">
                        <td className="px-4 py-2 text-zinc-200 font-mono">{a.exe}</td>
                        <td className="px-4 py-2 text-right text-zinc-300 font-mono">{fmtH(a.minutes)}</td>
                        <td className="px-4 py-2">
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div className="h-full bg-sky-500" style={{ width: `${(a.minutes / top) * 100}%` }} />
                          </div>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, hint, tint }) {
  const tints = {
    emerald: 'bg-emerald-500/10 ring-emerald-500/30 text-emerald-300',
    amber:   'bg-amber-500/10 ring-amber-500/30 text-amber-300',
    sky:     'bg-sky-500/10 ring-sky-500/30 text-sky-300',
  };
  return (
    <div className={`rounded-xl border border-zinc-800 ring-1 p-4 ${tints[tint]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
      </div>
      <p className="text-2xl font-semibold text-zinc-100">{value}</p>
      <p className="text-[11px] text-zinc-500 mt-1">{hint}</p>
    </div>
  );
}
